import { v4 as uuidv4 } from 'uuid';
import { contracts, events, artists, templates, performances, partners } from '../../../services/api.admin';
import { contracts as apiContracts } from '../../../services/api';
import { fetchTableData, deleteTableData } from '../tables';
import { MODELS, HTML_CLASSES } from '../../../constants/admin/settings';
import { toBase64, downloadBase64File } from '../../../lib/file.utils';
import { fetchModel, submit, fetchDependencies, onInput, setModelData } from '../forms';
import {
    apiDataToState,
    allVersionsSelector,
    allFormDataSelector,
    filterSigners,
    logoToBase64
} from '../../../selectors/admin/models/contracts';
import { pick, find } from 'lodash';
import { apiActionCreator } from '../../helpers';
import { hasPermissions, PERMISSIONS } from '../../../constants/authentication/permissions';
import {
    NO_SIGNERS_TEXT,
    LOADING_TEXT,
    CONTRACT_STATE_DRAFT,
    CLEARING_TO_BE_CONFIRMED,
    CONTRACT_STATE_SENT,
    CONTRACT_TYPES
} from '../../../constants/settings';
import { jsonParse } from '../../../lib/string.utils';
import { accessToClearance, currentUserSelector } from '../../../selectors/authentication';
import { currentContractSelector, dependenciesSelector } from '../../../selectors/admin/forms';
import { contextSubjectSelector } from '../../../selectors/admin/context';
import { ADMIN_FLASH, ADMIN_MODAL_FORMS } from '../../../constants/actions';
import { filtersSelector, selectedSelector } from '../../../selectors/admin/tables';

export function fetchList(contextId, pagination, context, type) {
    return (dispatch, getState) => {
        const filters = filtersSelector(getState());
        return fetchTableData(
            contextId
                ? () => contracts.getOrg(contextId, pagination, filters, type)
                : () => contracts.get(pagination, filters, type),
            'contracts',
            {
                pagination
            }
        )(dispatch, getState);
    };
}

export function fetchArchivedList(contextId, pagination) {
    return (dispatch, getState) => {
        const filters = filtersSelector(getState());
        const user = currentUserSelector(getState());
        const type =
            filters.type ||
            (accessToClearance(user, contextId) ? CONTRACT_TYPES.CLEARANCE : CONTRACT_TYPES.PERFORMANCE);
        return fetchTableData(
            contextId
                ? () => contracts.getOrgArchived(contextId, { ...pagination, type })
                : () => contracts.getArchived({ ...pagination, type }),
            'contracts',
            { pagination }
        )(dispatch, getState);
    };
}

export function deleteContracts() {
    return deleteTableData(contracts.delete, MODELS.CONTRACT);
}

export function archiveContracts() {
    return deleteTableData(contracts.put, MODELS.CONTRACT, {
        okText: 'Contract(s) successfully archived.',
        body: { archived: true },
        modelBodyField: [
            { name: 'eventId', location: 'event.id' },
            { name: 'organizationId', location: 'organizationId' },
            { name: 'artistId', location: 'artist.id' }
        ]
    });
}

export function fetchContractDependencies(contextId, user, type) {
    let dependencies = [contextId ? () => events.getOrgAll(contextId, type) : () => events.getAll(type)];
    if (user && hasPermissions(user, PERMISSIONS.TEMPLATES_VIEW)) {
        dependencies = dependencies.concat([contextId ? () => templates.getOrgAll(contextId) : templates.getAll]);
    }
    return fetchDependencies(dependencies, {
        append: true,
        parseFunction: res => {
            res.templates = (res.templates || []).map(logoToBase64);
            return res;
        }
    });
}

export function fetchArtists(eventId, contextId) {
    return fetchDependencies([() => artists.getEventAll(eventId, contextId)], { append: true, sub: true });
}

export function fetchEntries(eventId, artistId) {
    return fetchDependencies([() => performances.getAllForArtist(eventId, artistId)], { append: true, sub: true });
}

export function fetchEmailsForContract(eventId, eventType) {
    return fetchDependencies([() => events.getEmails(eventId, eventType)], { append: true, sub: true });
}

export function fetchPartners(eventId, contextId, setClearing = false) {
    return fetchDependencies([() => partners.getAllForEvent(eventId, contextId)], {
        append: true,
        sub: true,
        afterDone: (dispatch, getState) => {
            if (setClearing) {
                const { partners = [] } = dependenciesSelector(getState());
                const clearing = partners.map(p => ({
                    live: CLEARING_TO_BE_CONFIRMED,
                    comments: '',
                    useUntil: 0,
                    nonLinear: CLEARING_TO_BE_CONFIRMED,
                    partnerId: p.id,
                    id: uuidv4()
                }));
                dispatch(onInput(MODELS.CONTRACT, 'clearing', clearing, true));
            }
        }
    });
}

export function fetchArtistsAndEntries(eventId, artistId, contextId) {
    return fetchDependencies(
        [() => artists.getEventAll(eventId, contextId), () => performances.getAllForArtist(eventId, artistId)],
        {
            append: true,
            sub: true
        }
    );
}

export function saveNewVersion(contractId, data, state = CONTRACT_STATE_DRAFT, cancelRedirect = false) {
    return submit(
        () =>
            contractId ? contracts.put(contractId, permitContractBody(data)) : contracts.post(permitContractBody(data)),
        contractId
            ? `Contract successfully ${state === CONTRACT_STATE_SENT ? 'sent' : 'updated'}.`
            : `Contract successfully ${state === CONTRACT_STATE_SENT ? 'sent' : 'created'}.`,
        {
            beforeDone: (response, dispatch, getState, done) => {
                const user = currentUserSelector(getState());
                return apiActionCreator(
                    dispatch,
                    () =>
                        contracts.createVersion(contractId || response.id, {
                            state,
                            ..._permitVersionBody(data),
                            userId: user.id
                        }),
                    () => {
                        done();
                        dispatch(fetchMember(contractId || response.id));
                        dispatch(fetchVersions(contractId || response.id));
                    }
                );
            },
            cancelRedirect
        }
    );
}

export function createFromPerformanceModal(data, send) {
    return saveNewVersion(null, data, send ? CONTRACT_STATE_SENT : CONTRACT_STATE_DRAFT, true);
}

export function archive(id, body) {
    return submit(() => contracts.put(id, body), 'Contract successfully (un)archived.', { cancelRedirect: true });
}

export function fetchMember(id) {
    return fetchModel(() => contracts.getMember(id), MODELS.CONTRACT, {
        transform: apiDataToState
    });
}

export function fetchSigners(orgId, artistId, eventId, contractType) {
    return async (dispatch, getState) => {
        const contextId = contextSubjectSelector(getState());
        dispatch(onInput(MODELS.CONTRACT, 'fetchingSignersFor', artistId, true));
        dispatch(onInput(MODELS.CONTRACT, 'signers', LOADING_TEXT, true));
        await apiActionCreator(
            dispatch,
            () => contracts.getSigners(artistId, contextId, contractType),
            response => {
                dispatch(onInput(MODELS.CONTRACT, 'fetchedSignersFor', artistId, true));
                dispatch(onInput(MODELS.CONTRACT, 'fetchingSignersFor', null, true));
                const filteredSigners = filterSigners(response.users, orgId, artistId, eventId, contractType);
                dispatch(
                    onInput(
                        MODELS.CONTRACT,
                        'signers',
                        filteredSigners.length ? filteredSigners.join(', ') : NO_SIGNERS_TEXT,
                        true
                    )
                );
            }
        );
    };
}

export function fetchVersions(contractId) {
    return fetchDependencies([() => contracts.getAllVersions(contractId)], {
        append: true,
        parseFunction: res => {
            res.versions = (res.versions || []).map(logoToBase64);
            return res;
        }
    });
}

export function switchVersion(value) {
    return (dispatch, getState) => {
        const selected = find(allVersionsSelector(getState()), v => v.id === value);
        const current = allFormDataSelector(getState());
        dispatch(
            setModelData(MODELS.CONTRACT, { ...current, logo: selected.logo, data: selected.data, version: selected })
        );
    };
}

export const permitContractBody = (data, skipPerf = false) => {
    const body = { ...data };
    if (!skipPerf) {
        body.performances = (body.performances || []).map(p => jsonParse(p, {}));
    }
    body.dueDate = body.dueDate || null;
    return pick(body, [
        'eventId',
        'dueDate',
        'organizationId',
        'artistId',
        'notes',
        'type',
        'performances',
        'clearing',
        'archived',
        'signedOn',
        'signedBy'
    ]);
};

const _permitVersionBody = data => {
    const bodyData = { ...data };
    bodyData.data = (data.data || '<p></p>')
        .replace(new RegExp(`${HTML_CLASSES.RTE_COMMENTS} ${HTML_CLASSES.RTE_JS_COMMENTS}`, 'g'), '')
        .replace(/id="js-id-[0-9]*" /g, '');
    bodyData.comments = { admin: bodyData.comments || '' };
    bodyData.logo = data.logo;
    return pick(bodyData, ['data', 'comments', 'logo']);
};

export function removeContractFile() {
    return onInput(MODELS.CONTRACT, 'file', '');
}

export function sendContractReminder(data) {
    return (dispatch, getState) => {
        dispatch({ type: ADMIN_MODAL_FORMS.SUBMIT });
        const selection = selectedSelector(getState()) || currentContractSelector(getState());
        const contract = currentContractSelector(getState());
        const contractId = !!selection.length ? selection : [contract];
        return apiActionCreator(
            dispatch,
            () => contracts.sendReminder(contractId, data.comments),
            () => {
                dispatch({ type: ADMIN_MODAL_FORMS.SUCCESS });
                dispatch({
                    type: ADMIN_FLASH.OPEN,
                    message: 'Reminder successfully sent!'
                });
                dispatch(fetchMember(contractId));
            }
        );
    };
}

export function uploadContract(id, data) {
    return async dispatch => {
        dispatch({ type: ADMIN_MODAL_FORMS.SUBMIT });
        const base64 = await toBase64(data.file);
        return apiActionCreator(
            dispatch,
            () =>
                contracts.upload(id, {
                    content: base64,
                    fileName: data.file.name,
                    fileType: data.file.type,
                    comments: data.comments,
                    state: data.state
                }),
            () => {
                dispatch({ type: ADMIN_MODAL_FORMS.SUCCESS });
                dispatch({
                    type: ADMIN_FLASH.OPEN,
                    message: 'Contract version uploaded.'
                });
                dispatch(fetchMember(id));
                dispatch(fetchVersions(id));
            }
        );
    };
}

export function downloadUploadedContract(contractId, versionId) {
    return dispatch => {
        return apiActionCreator(
            dispatch,
            () => apiContracts.downloadUpload(contractId, versionId),
            resp => {
                downloadBase64File(resp.type, resp.data, resp.fileName || 'clearem_contract.pdf');
            }
        );
    };
}

export function downloadContract(contractId, versionId, isPublic = false) {
    return dispatch => {
        return apiActionCreator(
            dispatch,
            () =>
                isPublic
                    ? apiContracts.downloadPublic(contractId, versionId)
                    : apiContracts.download(contractId, versionId),
            resp => {
                downloadBase64File(resp.type, resp.data, resp.fileName || 'clearem_contract.pdf');
            }
        );
    };
}
